import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Eye, EyeOff, Play, X, Pause, ArrowLeft, Key, Shield, Mail, Lock } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import PortalMismatchDialog from '../components/PortalMismatchDialog';
import SignupCaptcha, { getFingerprint } from '../components/SignupCaptcha';
import EmailVerificationModal from '../components/EmailVerificationModal';
import ApplicationPasswordModal from '../components/ApplicationPasswordModal';
import IdentitySupportFallback from '../components/IdentitySupportFallback';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo image URLs
const LOGO_DARK = "/orb-logo-header.png?v=2026-05-17";
// iter161 — Single canonical ORB header logo, served from /public.
// Pure black letters on transparent background. AuthPage card is
// always rendered in light mode, so no dark-mode flip is needed
// here — the logo stays black on the white card. Layout.js uses
// `dark:invert` to flip the same asset to white on dark surfaces.
// Asset is generated from the master 512px source in
// /app/frontend/public/login-orb-logo.png (committed to the repo).
const LOGO_LIGHT = "/orb-logo-header.png?v=2026-05-17";

// Welcome screen with intro video
const WelcomeScreen = ({ onComplete, onSkip, updateUser }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [maxWatchedTime, setMaxWatchedTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // iter174 — Resolve intro video from admin override stored in
  // `system_settings.onboarding_videos`. Falls back to the static
  // /intro_video.mp4. AuthPage runs UNAUTHENTICATED (this WelcomeScreen
  // is only mounted after sign-in for users with intro_video_watched=
  // false, so the API call is authorized). We use a fetch with the
  // current token to avoid pulling AuthContext API into a non-React
  // helper signature.
  const [INTRO_VIDEO_URL, setIntroVideoUrl] = useState("/intro_video.mp4");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const tok = localStorage.getItem('orb_token');
        if (!tok) return;
        const res = await fetch(`${API}/me/onboarding/videos`, {
          headers: { Authorization: `Bearer ${tok}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const override = data?.intro_video?.url;
        if (alive && override) setIntroVideoUrl(override);
      } catch (_e) { /* silent fallback */ }
    })();
    return () => { alive = false; };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayVideo = () => {
    setShowVideo(true);
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }, 100);
  };

  const handlePause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleVideoEnd = async () => {
    setVideoEnded(true);
    setIsPlaying(false);
    // Mark intro video as watched
    try {
      if (updateUser) {
        await updateUser({ intro_video_watched: true });
      }
    } catch (e) {
      console.error('Failed to update video status:', e);
    }
    onComplete();
  };

  const handleCloseVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setShowVideo(false);
    setIsPlaying(false);
    if (!videoEnded) {
      toast.info('Complete the video to mark Step 1 as done');
    }
  };

  // Prevent fast forward - only allow seeking to already watched portions
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      const current = video.currentTime;
      
      // Update max watched time
      if (current > maxWatchedTime) {
        setMaxWatchedTime(current);
      }
      
      setCurrentTime(current);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  // Prevent seeking ahead
  const handleSeeking = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      // If trying to seek ahead of max watched, reset to max watched
      if (video.currentTime > maxWatchedTime + 0.5) {
        video.currentTime = maxWatchedTime;
        toast.warning('Please watch the video without skipping');
      }
    }
  };

  const remainingTime = Math.max(0, duration - currentTime);

  if (showVideo) {
    return (
      <div className="fixed inset-0 bg-black z-[100] flex items-center justify-center">
        <div className="relative w-full max-w-md mx-4">
          {/* Video controls */}
          <div className="absolute top-4 right-4 z-10 flex gap-2">
            <Button
              variant="ghost"
              className="bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 h-auto"
              onClick={handlePause}
              data-testid="video-pause-btn"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              className="bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 h-auto"
              onClick={handleCloseVideo}
              data-testid="video-close-btn"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Countdown Timer */}
          {duration > 0 && !videoEnded && (
            <div className="absolute top-4 left-4 z-10 bg-black/60 px-3 py-1 rounded-full">
              <span className="text-white text-sm font-medium">
                {formatTime(remainingTime)} remaining
              </span>
            </div>
          )}
          
          <video
            ref={videoRef}
            className="w-full rounded-lg"
            onEnded={handleVideoEnd}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onSeeking={handleSeeking}
            playsInline
            data-testid="intro-video"
          >
            <source src={INTRO_VIDEO_URL} type="video/mp4" />
            Your browser does not support video playback.
          </video>
          
          {videoEnded && (
            <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-lg">
              <div className="text-center text-white">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden">
                  <img src={LOGO_DARK} alt="Logo" className="w-full h-full object-contain" />
                </div>
                <h3 className="text-xl font-bold mb-2">Video Complete!</h3>
                <p className="text-gray-300 mb-4">Step 1 has been marked as complete.</p>
                <Button onClick={handleCloseVideo} className="bg-[#757150] hover:bg-[#8a8560]" data-testid="video-continue-btn">
                  Continue to App
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#1c1a1c] flex items-center justify-center p-4"
      style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
    >
      <Card className="w-full max-w-lg bg-[#252528] border-[#3a3a3d] text-white animate-slide-up">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden">
            <img src={LOGO_DARK} alt="The Uplift" className="w-full h-full object-contain" />
          </div>
          <CardTitle className="text-3xl font-bold text-white">Welcome to The Uplift!</CardTitle>
          <CardDescription className="text-gray-400 text-base leading-relaxed">
            We're excited to have you join the ORB community! The Uplift is your all-in-one platform 
            for managing your business, tracking your progress, and connecting with your team.
            <br /><br />
            Before you get started, please watch our brief intro video to learn about all the 
            amazing features available to you.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            className="w-full h-14 text-lg bg-[#757150] hover:bg-[#8a8560] rounded-full"
            onClick={handlePlayVideo}
            data-testid="watch-intro-video-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Watch the Intro Video
          </Button>
          <Button 
            variant="ghost" 
            className="w-full text-gray-400 hover:text-white hover:bg-white/10"
            onClick={onSkip}
            data-testid="skip-intro-video-btn"
          >
            Skip for now
          </Button>
          <p className="text-xs text-gray-500 text-center">
            You can watch the intro video anytime from your home dashboard
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, updateUser, api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTotpInput, setShowTotpInput] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [accountLocked, setAccountLocked] = useState(false);
  // Portal-mismatch blocker: if a restricted-state user logs into the
  // standard /auth page, we log them out and show a modal redirecting
  // them to /signup/{their-state-code}.
  const [mismatchRedirect, setMismatchRedirect] = useState(null);
  
  // Check if user was redirected due to account lock
  useEffect(() => {
    if (searchParams.get('locked') === 'true') {
      setAccountLocked(true);
      // Clean up URL
      window.history.replaceState({}, '', '/');
    }
  }, [searchParams]);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState('email'); // email, totp, recovery, email-support
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotTotpCode, setForgotTotpCode] = useState('');
  const [forgotRecoveryKey, setForgotRecoveryKey] = useState('');
  const [recoveryOptions, setRecoveryOptions] = useState({ totp_enabled: false, recovery_key_enabled: false });
  
  // Password reset state (after logging in via recovery)
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetTotpCode, setResetTotpCode] = useState('');
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'affiliate',
    state: '',
    region: ''
  });

  // ── CAPTCHA gate state ──
  // The captcha modal opens AFTER the user submits the signup form;
  // the actual /auth/register call only fires once the modal hands
  // back a single-use token bound to email + IP + fingerprint.
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [registerFormMountedAt, setRegisterFormMountedAt] = useState(0);
  // iter 97 — Application Password modal opens AFTER captcha pass.
  // We hold the captcha result here while the user types the
  // application password, then send everything together to /auth/register.
  const [appPasswordOpen, setAppPasswordOpen] = useState(false);
  const [pendingCaptcha, setPendingCaptcha] = useState(null); // { captcha_token, fingerprint }
  // iter172 — Email verification gate (6-digit code) sits between
  // the captcha pass and the app-password modal. The code is sent
  // by the modal on mount; `verificationToken` is the single-use
  // token that gets attached to /auth/register.
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verificationToken, setVerificationToken] = useState(null);
  useEffect(() => {
    // Stamp the moment the signup tab mounts so the captcha can
    // measure how long the form was filled in. This is one of the
    // 6 backend humanity heuristics.
    setRegisterFormMountedAt(performance.now());
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Portal-mismatch preflight: call /auth/login directly, check
      // /auth/state-scope with the returned token, and ONLY hydrate
      // AuthContext if the user belongs on the standard /auth portal.
      // This avoids a race where PublicRoute sees isAuthenticated and
      // redirects to '/' before the mismatch dialog can render.
      let loginRes;
      try {
        loginRes = await axios.post(`${API}/auth/login`, {
          email: loginData.email,
          password: loginData.password,
          totp_code: showTotpInput ? totpCode : undefined,
        });
      } catch (error) {
        const detail = error.response?.data?.detail;
        if (detail === 'TOTP_REQUIRED') {
          setShowTotpInput(true);
          toast.info('Please enter your authenticator code');
        } else if (detail === 'Invalid authenticator code') {
          toast.error('Invalid authenticator code. Please try again.');
          setTotpCode('');
        } else {
          toast.error(detail || 'Login failed');
        }
        return;
      }
      const previewToken = loginRes.data?.access_token;
      if (previewToken) {
        try {
          const r = await axios.get(`${API}/auth/state-scope`, {
            headers: { Authorization: `Bearer ${previewToken}` },
          });
          if (r.data?.is_restricted && r.data?.state) {
            setMismatchRedirect(`/signup/${encodeURIComponent(r.data.state)}`);
            return;
          }
        } catch { /* scope unknown — let login proceed */ }
      }
      // Mismatch check passed — now do the real AuthContext login so
      // every other page/context hydrates normally.
      await login(loginData.email, loginData.password, showTotpInput ? totpCode : null);
      toast.success('Welcome back!');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    // Quick client-side password sanity check so the user gets
    // immediate feedback BEFORE we burn a captcha attempt. The
    // server still re-validates the same rules.
    const pw = registerData.password || '';
    const isStrong = pw.length >= 12
      && /[A-Z]/.test(pw)
      && /[0-9]/.test(pw)
      && /[^A-Za-z0-9]/.test(pw);
    if (!isStrong) {
      toast.error('Password must be at least 12 characters and include 1 uppercase letter, 1 number, and 1 special character.');
      return;
    }
    // Open the captcha modal — the actual register call fires from
    // onPass below after the modal returns a fresh captcha_token.
    setCaptchaOpen(true);
  };

  const handleCaptchaPass = async ({ captcha_token, fingerprint }) => {
    // iter172 — captcha is solved; now run the 6-digit email
    // verification step BEFORE the application password modal so
    // the account is only created after all three gates pass.
    setCaptchaOpen(false);
    setPendingCaptcha({ captcha_token, fingerprint });
    setVerifyOpen(true);
  };

  const handleEmailVerified = ({ verification_token }) => {
    // Hold the verification token and proceed to the app password
    // modal. /auth/register will consume it server-side; if the
    // user closes the app-pw modal without finishing, the token
    // simply expires in 15 min.
    setVerificationToken(verification_token);
    setVerifyOpen(false);
    setAppPasswordOpen(true);
  };

  const handleVerifyClose = () => {
    // Closing without finishing rolls everything back so the user
    // can re-submit the form. Captcha was already consumed (it's
    // single-use), so resubmit forces a fresh captcha challenge.
    setVerifyOpen(false);
    setPendingCaptcha(null);
    setVerificationToken(null);
  };

  const handleAppPasswordSubmit = async (application_password) => {
    if (!pendingCaptcha) return;
    setLoading(true);
    try {
      await register({
        ...registerData,
        captcha_token: pendingCaptcha.captcha_token,
        fingerprint: pendingCaptcha.fingerprint,
        application_password,
        email_verification_token: verificationToken,
      });
      setAppPasswordOpen(false);
      setPendingCaptcha(null);
      setVerificationToken(null);
      setShowWelcome(true);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Registration failed';
      toast.error(detail);
      // Wrong app password → keep the modal open, but the captcha
      // token has NOT been consumed (server checks app pw first),
      // so the user can retry without re-solving the captcha.
      // Other errors (captcha expired, blocked IP) → close everything
      // so the user can start over.
      if (!/application password/i.test(detail)) {
        setAppPasswordOpen(false);
        setPendingCaptcha(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAppPasswordClose = async () => {
    // iter 98 — closing the app-pw modal without successfully
    // creating an account counts as a failed signup attempt. We
    // already burnt the captcha if registration started; this
    // additional ping prevents bots from solving the captcha,
    // peeking at the app-pw step, and bailing out for free.
    setAppPasswordOpen(false);
    setPendingCaptcha(null);
    setLoading(false);
    try {
      const fp = pendingCaptcha?.fingerprint || (typeof getFingerprint === 'function' ? getFingerprint() : '');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/security/signup-abandon`, {
        email: registerData.email,
        fingerprint: fp,
      });
    } catch (_e) { /* best-effort */ }
  };

  const handleCaptchaClose = (reason) => {
    setCaptchaOpen(false);
    // The modal already toasted any user-facing message; we just
    // make sure the submit button isn't stuck in a loading state.
    setLoading(false);
  };

  const handleWelcomeSkip = () => {
    setShowWelcome(false);
    toast.success('Account created! Complete the intro video from your dashboard.');
    navigate('/');
  };

  const handleWelcomeContinue = () => {
    setShowWelcome(false);
    navigate('/');
  };

  const [bypassState, setBypassState] = useState({ active: false, bypass_mfa: false, bypass_recovery_key: false });
  const [overridePwd, setOverridePwd] = useState('');
  // iter131 — Holds the JWT issued by verify-bypass / verify-both
  // ONLY long enough to authenticate the immediately-following
  // /auth/reset-password call. We deliberately do NOT hydrate
  // AuthContext here — the user must finish the reset flow and
  // then log in again so the persistent post-login modals
  // (PersistentMfaSetup + PersistentRecoveryReveal) fire on a
  // clean session.
  const [resetAuthToken, setResetAuthToken] = useState(null);

  // Forgot Password Handlers
  const handleForgotPasswordEmailCheck = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password/check-email', null, { params: { email: forgotEmail } });
      const data = res.data;
      setRecoveryOptions(data);

      if (!data.email_found) {
        // Don't leak account existence. Silently route them to the
        // email-support fallback step — the IdentitySupportFallback
        // resolver will return the "Email Support" button for any
        // unknown email so the UX is identical to a verified-no-MFA
        // user choosing "I can't provide both".
        setForgotPasswordStep('email-support');
        return;
      }

      // Check if admin has issued a one-time bypass — replaces some/
      // all of the recovery-key/MFA prompts with an override password.
      try {
        const bp = await api.post('/auth/check-bypass', { email: forgotEmail });
        setBypassState({
          active: !!bp.data?.active,
          bypass_mfa: !!bp.data?.bypass_mfa,
          bypass_recovery_key: !!bp.data?.bypass_recovery_key,
        });
      } catch (_) { /* non-fatal */ }

      setForgotPasswordStep('recovery');
    } catch (error) {
      toast.error('Failed to check email');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordRecoveryAndTotp = async (e) => {
    e.preventDefault();

    // Bypass path — override password replaces some/all of the
    // standard prompts.
    if (bypassState.active) {
      if (!overridePwd) { toast.error('Override password required'); return; }
      if (!bypassState.bypass_recovery_key && !forgotRecoveryKey.trim()) {
        toast.error('Please enter your recovery key');
        return;
      }
      if (!bypassState.bypass_mfa && forgotTotpCode.length !== 6) {
        toast.error('Please enter your 6-digit authenticator code');
        return;
      }
      setLoading(true);
      try {
        const res = await api.post('/auth/forgot-password/verify-bypass', {
          email: forgotEmail,
          override_password: overridePwd,
          recovery_key: bypassState.bypass_recovery_key ? undefined : forgotRecoveryKey,
          totp_code: bypassState.bypass_mfa ? undefined : forgotTotpCode,
        });
        // iter131 — Stash the JWT in component state ONLY so the
        // immediately-following /auth/reset-password call carries
        // it. We do NOT hydrate AuthContext — the user must finish
        // the reset and log in fresh so the persistent post-login
        // modals (MFA + recovery setup) fire on a clean session.
        // The previous code wrote to `localStorage['token']`
        // (wrong key — should have been `orb_token`), so the
        // axios `api` instance had no bearer and /auth/reset-password
        // returned 401 — which is what JBP hit in prod.
        setResetAuthToken(res.data.access_token);
        toast.success('Bypass accepted');
        setShowForgotPassword(false);
        setShowPasswordReset(true);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Bypass verification failed');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Standard path — recovery key + TOTP both required.
    if (!forgotRecoveryKey.trim()) {
      toast.error('Please enter your recovery key');
      return;
    }
    if (forgotTotpCode.length !== 6) {
      toast.error('Please enter your 6-digit authenticator code');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password/verify-both', {
        email: forgotEmail,
        recovery_key: forgotRecoveryKey,
        totp_code: forgotTotpCode
      });
      // iter131 — Same pattern as the bypass branch: scoped token
      // for the reset-password call only.
      setResetAuthToken(res.data.access_token);
      toast.success('Verified! Please set a new password.');
      setShowForgotPassword(false);
      setShowPasswordReset(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Verification failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!resetAuthToken) {
      toast.error('Session expired — please start the recovery flow again.');
      setShowPasswordReset(false);
      return;
    }
    // iter131 — If the user arrived via a bypass that waived MFA, the
    // backend no longer requires a TOTP code on /auth/reset-password
    // (MFA was already torn down). Omit `totp_code` from the payload
    // so the new endpoint logic short-circuits the TOTP check.
    const mfaWasBypassed = bypassState.active && bypassState.bypass_mfa;
    setLoading(true);
    try {
      // Use a direct axios call with the scoped temp token from
      // verify-bypass / verify-both. The AuthContext-bound `api`
      // instance has no bearer here because we deliberately do
      // NOT hydrate AuthContext until the user re-logs in fresh.
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        new_password: newPassword,
        confirm_password: confirmPassword,
        ...(mfaWasBypassed ? {} : { totp_code: resetTotpCode }),
      }, { headers: { Authorization: `Bearer ${resetAuthToken}` } });
      toast.success('Password reset successfully! Please log in.');
      setShowPasswordReset(false);
      setNewPassword('');
      setConfirmPassword('');
      setResetTotpCode('');
      setResetAuthToken(null);
      // Clear any legacy/stray token so user has to log in again.
      localStorage.removeItem('token');
      localStorage.removeItem('orb_token');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const resetForgotPasswordState = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep('email');
    setForgotEmail('');
    setForgotTotpCode('');
    setForgotRecoveryKey('');
    setRecoveryOptions({ totp_enabled: false, recovery_key_enabled: false });
    // iter131 — Clear bypass state + temp token so a subsequent
    // password-recovery attempt doesn't render stale MFA/recovery
    // prompts (or, conversely, hide them based on a stale bypass).
    setBypassState({ active: false, bypass_mfa: false, bypass_recovery_key: false });
    setOverridePwd('');
    setResetAuthToken(null);
  };

  // Show welcome screen for new users
  if (showWelcome) {
    return (
      <WelcomeScreen 
        onComplete={handleWelcomeContinue} 
        onSkip={handleWelcomeSkip}
        updateUser={updateUser}
      />
    );
  }

  // Show password reset screen (after successful TOTP/recovery verification)
  if (showPasswordReset) {
    return (
      <div
        className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] flex items-center justify-center p-4"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
      >
        <Card className="w-full max-w-md bg-white border-gray-200 shadow-lg animate-slide-up">
          <CardHeader className="text-center space-y-0 pb-0">
            <div className="mx-auto w-16 h-12 flex items-center justify-center overflow-hidden translate-y-1">
              <img src={LOGO_LIGHT} alt="ORB" className="w-full h-full object-contain" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900 tracking-tight" style={{ transform: 'translateY(-18px)' }}>Reset Your Password</CardTitle>
            <CardDescription className="text-gray-500">Enter your new password below</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordReset} className="space-y-4" autoComplete="off">
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-gray-700">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                  data-testid="reset-new-password-input"
                    autoComplete="new-password"
                    name="orb-new-pwd"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-gray-700">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                  data-testid="reset-confirm-password-input"
                    autoComplete="new-password"
                    name="orb-confirm-pwd"
                />
              </div>
              {/* iter130 — Authenticator code is only required when
                  the user is going through the *standard* recover
                  flow. If they arrived via an admin bypass that
                  waived MFA, the TOTP secret was already torn down
                  on consume, so this prompt would dead-end the
                  reset. Hide it; the new MFA setup is forced after
                  login by PersistentMfaSetup. */}
              {!(bypassState.active && bypassState.bypass_mfa) && (
                <div className="space-y-2">
                  <Label htmlFor="reset-totp" className="text-gray-700">Authenticator Code</Label>
                  <Input
                    id="reset-totp"
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={resetTotpCode}
                    onChange={(e) => setResetTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    maxLength={6}
                    className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 text-center text-xl tracking-widest font-mono"
                    data-testid="reset-totp-input"
                    autoComplete="off"
                    name="orb-reset-totp"
                  />
                  <p className="text-xs text-gray-500 text-center">
                    Enter the code from your authenticator app to confirm the change
                  </p>
                </div>
              )}
              {bypassState.active && bypassState.bypass_mfa && (
                <p className="text-xs text-gray-500 text-center bg-amber-50 border border-amber-200 rounded-lg p-2">
                  After saving your new password, you&apos;ll be prompted to set up a fresh authenticator on your next sign-in.
                </p>
              )}
              <Button 
                type="submit" 
                className="w-full rounded-full bg-[#586D52] hover:bg-[#4a5d46] text-white" 
                disabled={loading || (!(bypassState.active && bypassState.bypass_mfa) && resetTotpCode.length !== 6)}
                data-testid="reset-password-submit-btn"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show forgot password flow
  if (showForgotPassword) {
    return (
      <div
        className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] flex items-center justify-center p-4"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
      >
        <Card className="w-full max-w-md bg-white border-gray-200 shadow-lg animate-slide-up">
          <CardHeader className="text-center space-y-1 pb-2">
            <div className="mx-auto w-16 h-12 flex items-center justify-center overflow-hidden">
              <img src={LOGO_LIGHT} alt="ORB" className="w-full h-full object-contain" />
            </div>
            <CardTitle className="text-lg font-semibold text-gray-900 tracking-tight mt-1">Forgot Password</CardTitle>
            <CardDescription className="text-gray-500">
              {forgotPasswordStep === 'email' && 'Enter your email to recover your account'}
              {forgotPasswordStep === 'totp' && 'Enter your authenticator code'}
              {forgotPasswordStep === 'recovery' && 'Enter your recovery key'}
              {forgotPasswordStep === 'email-support' && 'Contact support for assistance'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Step 1: Email Input */}
            {forgotPasswordStep === 'email' && (
              <form onSubmit={handleForgotPasswordEmailCheck} className="space-y-4" autoComplete="off">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email" className="text-gray-700">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="your@email.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                    data-testid="forgot-email-input"
                    autoComplete="off"
                    name="orb-forgot-email"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full rounded-full bg-[#586D52] hover:bg-[#4a5d46] text-white" 
                  disabled={loading}
                  data-testid="forgot-email-submit-btn"
                >
                  {loading ? 'Checking...' : 'Continue'}
                </Button>
                <Button 
                  type="button"
                  variant="ghost"
                  className="w-full text-gray-500 hover:text-gray-700"
                  onClick={resetForgotPasswordState}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign In
                </Button>
              </form>
            )}

            {/* Step 2: Combined Recovery Key + TOTP Verification (or bypass override) */}
            {forgotPasswordStep === 'recovery' && (
              <form onSubmit={handleForgotPasswordRecoveryAndTotp} className="space-y-4" autoComplete="off">
                <div className="p-4 bg-amber-50 rounded-lg text-center mb-4">
                  <Key className="w-10 h-10 mx-auto mb-2 text-amber-600" />
                  <p className="text-sm text-amber-700 font-medium">
                    {bypassState.active ? 'Admin Bypass Active' : 'Verify Your Identity'}
                  </p>
                </div>

                {bypassState.active && (
                  <div className="space-y-2">
                    <Label className="text-gray-700">Override Password</Label>
                    <Input
                      type="text"
                      value={overridePwd}
                      onChange={(e) => setOverridePwd(e.target.value)}
                      placeholder="Provided by your admin"
                      className="bg-gray-50 border-gray-300 text-gray-900 font-mono"
                      data-testid="forgot-override-pwd-input"
                    autoComplete="new-password"
                    name="orb-override-pwd"
                    />
                  </div>
                )}

                {!bypassState.bypass_recovery_key && (
                <div className="space-y-2">
                  <Label htmlFor="recovery-key" className="text-gray-700">Recovery Key</Label>
                  <Input
                    id="recovery-key"
                    type="text"
                    placeholder="Enter your recovery key"
                    value={forgotRecoveryKey}
                    onChange={(e) => setForgotRecoveryKey(e.target.value.toUpperCase())}
                    className="bg-gray-50 border-gray-300 text-gray-900 text-center font-mono tracking-wider"
                    data-testid="forgot-recovery-input"
                    autoComplete="off"
                    name="orb-forgot-rec"
                  />
                </div>
                )}

                {!bypassState.bypass_mfa && (
                <div className="space-y-2">
                  <Label htmlFor="forgot-totp" className="text-gray-700">Authenticator Code</Label>
                  <Input
                    id="forgot-totp"
                    type="text"
                    placeholder="000000"
                    value={forgotTotpCode}
                    onChange={(e) => setForgotTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="bg-gray-50 border-gray-300 text-gray-900 text-center text-2xl tracking-widest font-mono"
                    data-testid="forgot-totp-input"
                    autoComplete="off"
                    name="orb-forgot-totp"
                  />
                  <p className="text-xs text-gray-500">
                    6-digit code from your authenticator app
                  </p>
                </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full rounded-full bg-[#586D52] hover:bg-[#4a5d46] text-white" 
                  disabled={
                    loading ||
                    (bypassState.active
                      ? (!overridePwd ||
                         (!bypassState.bypass_recovery_key && !forgotRecoveryKey) ||
                         (!bypassState.bypass_mfa && forgotTotpCode.length !== 6))
                      : (!forgotRecoveryKey || forgotTotpCode.length !== 6))
                  }
                  data-testid="forgot-recovery-submit-btn"
                >
                  {loading ? 'Verifying...' : 'Verify & Continue'}
                </Button>
                
                <Button 
                  type="button"
                  variant="ghost"
                  className="w-full text-gray-500 hover:text-gray-700"
                  onClick={() => setForgotPasswordStep('email-support')}
                >
                  I can't provide both
                </Button>
                
                <Button 
                  type="button"
                  variant="ghost"
                  className="w-full text-gray-500 hover:text-gray-700"
                  onClick={resetForgotPasswordState}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign In
                </Button>
              </form>
            )}

            {/* Step 4: Email Support Fallback — dynamic button based on
                role + state lookup. Affiliates/leads get a state
                supervisor mailto (plus-addressed for direct routing).
                Leadership sees a non-clickable label. Unknown emails
                get a generic support mailto. */}
            {forgotPasswordStep === 'email-support' && (
              <IdentitySupportFallback
                email={forgotEmail}
                onBack={resetForgotPasswordState}
                api={api}
              />
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const US_STATES = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming'
  ];

  return (
    <div
      className="min-h-screen max-h-screen overflow-hidden bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] flex items-center justify-center p-4"
      style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
    >
      <Card className="w-full max-w-md bg-white border-gray-200 shadow-lg animate-slide-up">
        <CardHeader className="text-center pb-6">
          {/* iter161 — "ORB Uplift" lockup matched to ORB Protege:
              ORB is the dominant brand mark, "Uplift" sits to its
              right as a smaller secondary wordmark, BOTH baselines
              aligned (items-end so the bottom edges line up).
              iter161.1 — scaled the whole lockup down (h-8 + text-xl)
              and added pb-6 on the CardHeader so the wordmark
              breathes above the Sign In / Sign Up tabs. */}
          <div className="flex items-end justify-center gap-1.5">
            <img
              src={LOGO_LIGHT}
              alt="ORB"
              className="h-7 w-auto object-contain block"
            />
            <CardTitle
              className="text-lg font-bold text-gray-900 tracking-tight leading-none"
              style={{ fontFamily: "'Manrope', sans-serif", marginBottom: '2px' }}
            >
              Uplift
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-gray-100">
              <TabsTrigger 
                value="login" 
                data-testid="login-tab"
                className="data-[state=active]:bg-[#586D52] data-[state=active]:text-white text-gray-600"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                data-testid="register-tab"
                className="data-[state=active]:bg-[#586D52] data-[state=active]:text-white text-gray-600"
              >
                Sign Up
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              {/* Account Locked Warning */}
              {accountLocked && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                  <Lock className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-700 dark:text-red-400">Account Locked</p>
                    <p className="text-sm text-red-600 dark:text-red-300">
                      Your account has been locked by an administrator. Please contact support for assistance.
                    </p>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleLogin} className="space-y-4" autoComplete="off">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-gray-700">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="your@email.com"
                    data-testid="login-email-input"
                    value={loginData.email}
                    onChange={(e) => {
                      setLoginData({ ...loginData, email: e.target.value });
                      setShowTotpInput(false);
                      setTotpCode('');
                      setAccountLocked(false); // Clear locked message when user starts typing
                    }}
                    required
                    disabled={showTotpInput}
                    autoComplete="off"
                    // iter132 — Chrome ignores autoComplete="off" on
                    // recognised credential fields. Using a randomised
                    // name attr prevents the browser's autofill from
                    // re-populating the input after refresh.
                    name="orb-login-email"
                    className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 disabled:opacity-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-gray-700">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter password"
                      data-testid="login-password-input"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      disabled={showTotpInput}
                      // iter132 — `new-password` tells the browser the
                      // field is for *setting* a password (not signing
                      // in), so saved credentials are NOT auto-filled.
                      autoComplete="new-password"
                      name="orb-login-secret"
                      className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                {/* TOTP Input - shown when user has 2FA enabled */}
                {showTotpInput && (
                  <div className="space-y-2 animate-slide-up">
                    <Label htmlFor="login-totp" className="text-gray-700">Authenticator Code</Label>
                    <Input
                      id="login-totp"
                      type="text"
                      placeholder="Enter 6-digit code"
                      data-testid="login-totp-input"
                      autoComplete="off"
                      name="orb-login-totp"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      maxLength={6}
                      className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 text-center text-xl tracking-widest font-mono"
                      autoFocus
                    />
                    <p className="text-xs text-gray-500 text-center">
                      Enter the code from your authenticator app
                    </p>
                    <button
                      type="button"
                      className="w-full text-sm text-gray-500 hover:text-gray-700"
                      onClick={() => {
                        setShowTotpInput(false);
                        setTotpCode('');
                      }}
                    >
                      ← Back to login
                    </button>
                  </div>
                )}
                
                <Button 
                  type="submit" 
                  className="w-full rounded-full bg-[#586D52] hover:bg-[#4a5d46] text-white" 
                  disabled={loading || (showTotpInput && totpCode.length !== 6)}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Signing in...' : showTotpInput ? 'Verify & Sign In' : 'Sign In'}
                </Button>
                
                {/* Forgot Password Link */}
                {!showTotpInput && (
                  <button
                    type="button"
                    className="w-full text-sm text-gray-500 hover:text-[#586D52] mt-2"
                    onClick={() => setShowForgotPassword(true)}
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                )}
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4" autoComplete="off">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="register-first-name" className="text-gray-700">First Name</Label>
                    <Input
                      id="register-first-name"
                      type="text"
                      placeholder="John"
                      data-testid="register-first-name-input"
                      value={registerData.first_name}
                      onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                      required
                      className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-last-name" className="text-gray-700">Last Name</Label>
                    <Input
                      id="register-last-name"
                      type="text"
                      placeholder="Doe"
                      data-testid="register-last-name-input"
                      value={registerData.last_name}
                      onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                      required
                      className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-email" className="text-gray-700">Email</Label>
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="your@email.com"
                    data-testid="register-email-input"
                    autoComplete="off"
                    name="orb-reg-email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    required
                    className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-password" className="text-gray-700">Password</Label>
                  <Input
                    id="register-password"
                    type="password"
                    placeholder="Create password"
                    data-testid="register-password-input"
                    autoComplete="new-password"
                    name="orb-reg-pwd"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    required
                    minLength={6}
                    className="bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Role</Label>
                    <Select 
                      value={registerData.role} 
                      onValueChange={(v) => setRegisterData({ ...registerData, role: v })}
                    >
                      <SelectTrigger 
                        data-testid="register-role-select"
                        className="bg-gray-50 border-gray-300 text-gray-900"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="affiliate" className="text-gray-900">Affiliate</SelectItem>
                        <SelectItem value="lead" className="text-gray-900">Lead</SelectItem>
                        <SelectItem value="supervisor" className="text-gray-900">Supervisor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">State</Label>
                    <Select 
                      value={registerData.state} 
                      onValueChange={(v) => setRegisterData({ ...registerData, state: v })}
                    >
                      <SelectTrigger 
                        data-testid="register-state-select"
                        className="bg-gray-50 border-gray-300 text-gray-900"
                      >
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 max-h-60">
                        {US_STATES.map(state => (
                          <SelectItem key={state} value={state} className="text-gray-900">
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full rounded-full bg-[#586D52] hover:bg-[#4a5d46] text-white" 
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
                <p
                  className="text-[11px] text-center text-neutral-600 mt-3 leading-snug"
                  data-testid="register-legal-copy"
                >
                  By creating an account, you agree to our{' '}
                  <a
                    href="https://www.collectiveorb.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-[#586D52] hover:text-[#4a5d46] font-medium"
                    data-testid="register-legal-privacy-link"
                  >
                    Privacy Policy
                  </a>
                  {' '}&amp;{' '}
                  <a
                    href="https://www.collectiveorb.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-[#586D52] hover:text-[#4a5d46] font-medium"
                    data-testid="register-legal-tos-link"
                  >
                    Terms of Service
                  </a>
                  .
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      <PortalMismatchDialog
        open={!!mismatchRedirect}
        redirectTo={mismatchRedirect}
        onRedirect={() => {
          const to = mismatchRedirect;
          setMismatchRedirect(null);
          if (to) window.location.assign(to);
        }}
      />
      <SignupCaptcha
        open={captchaOpen}
        onClose={handleCaptchaClose}
        onPass={handleCaptchaPass}
        email={registerData.email}
        formMountedAt={registerFormMountedAt}
      />
      <EmailVerificationModal
        open={verifyOpen}
        email={registerData.email}
        firstName={registerData.first_name}
        onVerified={handleEmailVerified}
        onClose={handleVerifyClose}
      />
      <ApplicationPasswordModal
        open={appPasswordOpen}
        onClose={handleAppPasswordClose}
        onSubmit={handleAppPasswordSubmit}
        submitting={loading}
      />
    </div>
  );
};

export default AuthPage;
